/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Admin Subscription Plans & Pricing Management View (Task 11.3.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CreditCard,
  Plus,
  RefreshCw,
  Edit2,
  Trash2,
  CheckCircle2,
  XCircle,
  Star,
  Search,
  Check,
  AlertTriangle,
  X,
  Sliders,
  Coins,
  Repeat,
  Zap,
} from 'lucide-react';
import { useI18n } from '../i18n';
import { adminClient } from '../lib/api-client';
import { DataTable, type ColumnDef } from '../components/table/DataTable';
import type {
  AdminPlanItem,
  AdminPlanCreateInput,
  AdminPlanUpdateInput,
} from '@sofia/shared-types';

const USD_TO_VND_RATE = 25400;

export const PlansManagementPage: React.FC = () => {
  const { t, locale } = useI18n();
  const queryClient = useQueryClient();

  // Filters state
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [cycleFilter, setCycleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Modals state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<AdminPlanItem | null>(null);
  const [deletingPlan, setDeletingPlan] = useState<AdminPlanItem | null>(null);
  const [toastMessage, setToastMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const showToast = (type: 'success' | 'error', text: string) => {
    setToastMessage({ type, text });
    setTimeout(() => setToastMessage(null), 4000);
  };

  // Currency Formatter
  const formatCurrency = (usdAmount: number) => {
    if (locale === 'vi') {
      const vnd = Math.round(usdAmount * USD_TO_VND_RATE);
      return (
        <div className="flex flex-col text-xs leading-tight">
          <span className="font-semibold text-foreground">
            {vnd.toLocaleString('vi-VN')} ₫
          </span>
          <span className="text-[10px] text-muted-fg font-mono">
            (${usdAmount.toFixed(2)})
          </span>
        </div>
      );
    }
    return (
      <div className="flex flex-col text-xs leading-tight">
        <span className="font-semibold text-foreground">
          ${usdAmount.toFixed(2)}
        </span>
        <span className="text-[10px] text-muted-fg font-mono">
          (~{(Math.round(usdAmount * USD_TO_VND_RATE)).toLocaleString()} ₫)
        </span>
      </div>
    );
  };

  const formatCycle = (cycle: string) => {
    switch (cycle) {
      case 'monthly':
        return t('admin.plans.cycleMonthly');
      case 'yearly':
        return t('admin.plans.cycleYearly');
      case 'one_time':
        return t('admin.plans.cycleOneTime');
      default:
        return cycle;
    }
  };

  // Fetch Plans list
  const {
    data: plansData,
    isLoading: isLoadingPlans,
    error: plansError,
    refetch: refetchPlans,
    isFetching,
  } = useQuery({
    queryKey: ['admin-plans', typeFilter, cycleFilter, statusFilter, searchQuery],
    queryFn: async () => {
      const res = await adminClient.get('/api/v1/admin/plans', {
        planType: typeFilter === 'all' ? undefined : typeFilter,
        billingCycle: cycleFilter === 'all' ? undefined : cycleFilter,
        isActive: statusFilter === 'all' ? undefined : statusFilter,
        search: searchQuery || undefined,
      });
      return (res?.data || []) as AdminPlanItem[];
    },
  });

  const plansList = plansData || [];

  // Mutations
  const updateMutation = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: AdminPlanUpdateInput }) => {
      return adminClient.patch(`/api/v1/admin/plans/${id}`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] });
      setEditingPlan(null);
      showToast('success', t('admin.plans.successUpdate'));
    },
    onError: (err: any) => {
      showToast('error', err?.message || 'Failed to update plan.');
    },
  });

  const createMutation = useMutation({
    mutationFn: async (payload: AdminPlanCreateInput) => {
      return adminClient.post('/api/v1/admin/plans', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] });
      setIsCreateModalOpen(false);
      showToast('success', t('admin.plans.successCreate'));
    },
    onError: (err: any) => {
      showToast('error', err?.message || 'Failed to create plan.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return adminClient.delete(`/api/v1/admin/plans/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] });
      setDeletingPlan(null);
      showToast('success', t('admin.plans.successDelete'));
    },
    onError: (err: any) => {
      showToast('error', err?.message || 'Failed to delete plan.');
    },
  });

  const handleToggleActive = (plan: AdminPlanItem) => {
    updateMutation.mutate({
      id: plan.id,
      payload: { isActive: !plan.isActive },
    });
  };

  // Table Columns Definition
  const columns = useMemo<ColumnDef<AdminPlanItem>[]>(() => [
    {
      id: 'name',
      header: t('admin.plans.colName'),
      accessorKey: 'name',
      cell: ({ row }) => (
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-xs text-foreground">{row.name}</span>
            {row.isFeatured && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono font-bold bg-amber-500/10 text-amber-500 border border-amber-500/20">
                <Star className="w-2.5 h-2.5 fill-current" />
                {t('admin.plans.featuredBadge')}
              </span>
            )}
          </div>
          <span className="font-mono text-[11px] text-muted-fg">{row.id}</span>
        </div>
      ),
    },
    {
      id: 'planType',
      header: t('admin.plans.colType'),
      accessorKey: 'planType',
      cell: ({ value }) => (
        <span
          className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-medium border ${
            value === 'subscription'
              ? 'bg-primary/10 text-primary border-primary/20'
              : 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
          }`}
        >
          {value === 'subscription' ? (
            <Repeat className="w-3 h-3" />
          ) : (
            <Coins className="w-3 h-3" />
          )}
          <span>
            {value === 'subscription'
              ? t('admin.plans.filterSubscription')
              : t('admin.plans.filterTokenPack')}
          </span>
        </span>
      ),
    },
    {
      id: 'price',
      header: t('admin.plans.colPrice'),
      accessorKey: 'price',
      cell: ({ value }) => formatCurrency(Number(value)),
    },
    {
      id: 'billingCycle',
      header: t('admin.plans.colCycle'),
      accessorKey: 'billingCycle',
      cell: ({ value }) => (
        <span className="text-xs font-medium text-foreground">
          {formatCycle(value)}
        </span>
      ),
    },
    {
      id: 'creditsIncluded',
      header: t('admin.plans.colCredits'),
      accessorKey: 'creditsIncluded',
      cell: ({ value }) => (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono font-bold bg-accent/10 text-accent border border-accent/20">
          <Zap className="w-3 h-3" />
          {Number(value).toLocaleString()}
        </span>
      ),
    },
    {
      id: 'features',
      header: t('admin.plans.colFeatures'),
      enableSorting: false,
      cell: ({ row }) => {
        const features = row.features || [];
        if (features.length === 0) {
          return <span className="text-[11px] text-muted-fg italic">—</span>;
        }
        return (
          <div className="flex flex-wrap gap-1 max-w-[200px]">
            {features.slice(0, 2).map((f, i) => (
              <span
                key={i}
                className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-surface-elevated text-muted-fg border border-surface-border truncate max-w-[190px]"
                title={f}
              >
                {f}
              </span>
            ))}
            {features.length > 2 && (
              <span className="px-1 py-0.5 rounded text-[10px] font-mono text-muted-fg bg-surface-border/20">
                +{features.length - 2}
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: 'stripePriceId',
      header: t('admin.plans.colStripe'),
      accessorKey: 'stripePriceId',
      cell: ({ value }) => (
        <span className="font-mono text-[11px] text-muted-fg">
          {value || '—'}
        </span>
      ),
    },
    {
      id: 'isActive',
      header: t('admin.plans.colStatus'),
      accessorKey: 'isActive',
      cell: ({ row, value }) => (
        <button
          type="button"
          onClick={() => handleToggleActive(row)}
          className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border transition-colors ${
            value
              ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20 hover:bg-emerald-500/20'
              : 'bg-muted-fg/10 text-muted-fg border-muted-fg/20 hover:bg-muted-fg/20'
          }`}
          title={t('admin.plans.toggleActive')}
        >
          {value ? (
            <>
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>{t('admin.plans.active')}</span>
            </>
          ) : (
            <>
              <XCircle className="w-3.5 h-3.5" />
              <span>{t('admin.plans.inactive')}</span>
            </>
          )}
        </button>
      ),
    },
    {
      id: 'actions',
      header: t('admin.plans.colActions'),
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setEditingPlan(row)}
            className="p-1.5 rounded-lg border border-surface-border bg-surface hover:bg-surface-elevated text-foreground hover:text-primary transition-colors"
            title={t('admin.plans.edit')}
          >
            <Edit2 className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setDeletingPlan(row)}
            className="p-1.5 rounded-lg border border-surface-border bg-surface hover:bg-destructive/10 text-muted-fg hover:text-destructive hover:border-destructive/30 transition-colors"
            title={t('admin.plans.delete')}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ),
    },
  ], [locale, t]);

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {toastMessage && (
        <div
          className={`fixed top-5 right-5 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-xl text-xs font-medium border animate-in fade-in slide-in-from-top-3 ${
            toastMessage.type === 'success'
              ? 'bg-emerald-950/90 text-emerald-200 border-emerald-500/30'
              : 'bg-destructive/90 text-destructive-foreground border-destructive'
          }`}
        >
          {toastMessage.type === 'success' ? (
            <Check className="w-4 h-4 text-emerald-400" />
          ) : (
            <AlertTriangle className="w-4 h-4" />
          )}
          <span>{toastMessage.text}</span>
        </div>
      )}

      {/* Header Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-6 rounded-2xl bg-surface border border-surface-border relative overflow-hidden">
        <div className="space-y-1 z-10">
          <div className="flex items-center gap-2 text-primary text-xs font-mono font-semibold uppercase tracking-wider">
            <CreditCard className="w-4 h-4" />
            <span>Billing & Revenue Architecture</span>
          </div>
          <h2 className="text-xl font-heading font-bold text-foreground">
            {t('admin.plans.title')}
          </h2>
          <p className="text-xs text-muted-fg max-w-xl">
            {t('admin.plans.subtitle')}
          </p>
        </div>

        <div className="flex items-center gap-2 z-10">
          <button
            type="button"
            onClick={() => refetchPlans()}
            disabled={isFetching}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-xl border border-surface-border bg-surface hover:bg-surface-elevated text-foreground transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
            <span>{t('admin.plans.refresh')}</span>
          </button>
          <button
            type="button"
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-xl bg-primary text-primary-fg hover:bg-primary/90 shadow-glow-primary transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>{t('admin.plans.createBtn')}</span>
          </button>
        </div>

        {/* Ambient background decoration */}
        <div className="absolute right-0 top-0 w-80 h-full bg-gradient-to-l from-primary/5 via-accent/5 to-transparent pointer-events-none" />
      </div>

      {/* Filter Toolbar */}
      <div className="p-4 rounded-xl bg-surface border border-surface-border flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-muted-fg absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('admin.plans.searchPlaceholder')}
            className="w-full pl-9 pr-3 py-1.5 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground placeholder:text-muted-fg focus:outline-none focus:border-primary/50"
          />
        </div>

        {/* Plan Type Filter */}
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-3 py-1.5 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary/50"
        >
          <option value="all">{t('admin.plans.filterAllTypes')}</option>
          <option value="subscription">{t('admin.plans.filterSubscription')}</option>
          <option value="token_pack">{t('admin.plans.filterTokenPack')}</option>
        </select>

        {/* Billing Cycle Filter */}
        <select
          value={cycleFilter}
          onChange={(e) => setCycleFilter(e.target.value)}
          className="px-3 py-1.5 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary/50"
        >
          <option value="all">{t('admin.plans.filterAllCycles')}</option>
          <option value="monthly">{t('admin.plans.cycleMonthly')}</option>
          <option value="yearly">{t('admin.plans.cycleYearly')}</option>
          <option value="one_time">{t('admin.plans.cycleOneTime')}</option>
        </select>

        {/* Status Filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-1.5 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary/50"
        >
          <option value="all">{t('admin.plans.filterAllStatuses')}</option>
          <option value="true">{t('admin.plans.active')}</option>
          <option value="false">{t('admin.plans.inactive')}</option>
        </select>
      </div>

      {/* TanStack Data Table */}
      <div className="bg-surface rounded-2xl border border-surface-border overflow-hidden">
        <DataTable
          columns={columns}
          data={plansList}
          isLoading={isLoadingPlans}
          error={plansError ? t('admin.plans.errorLoading') : null}
          enableSearch={false}
          enableCsvExport={true}
          exportFileName="sophia_plans_catalog.csv"
        />
      </div>

      {/* Edit Plan Modal */}
      {editingPlan && (
        <EditPlanModal
          plan={editingPlan}
          isOpen={true}
          isLoading={updateMutation.isPending}
          onClose={() => setEditingPlan(null)}
          onSave={(payload) => updateMutation.mutate({ id: editingPlan.id, payload })}
        />
      )}

      {/* Create Plan Modal */}
      {isCreateModalOpen && (
        <CreatePlanModal
          isOpen={true}
          isLoading={createMutation.isPending}
          onClose={() => setIsCreateModalOpen(false)}
          onCreate={(payload) => createMutation.mutate(payload)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingPlan && (
        <DeletePlanModal
          plan={deletingPlan}
          isLoading={deleteMutation.isPending}
          onClose={() => setDeletingPlan(null)}
          onConfirm={() => deleteMutation.mutate(deletingPlan.id)}
        />
      )}
    </div>
  );
};

// --- Sub-Component: Edit Plan Modal ---
interface EditPlanModalProps {
  plan: AdminPlanItem;
  isOpen: boolean;
  isLoading: boolean;
  onClose: () => void;
  onSave: (payload: AdminPlanUpdateInput) => void;
}

const EditPlanModal: React.FC<EditPlanModalProps> = ({
  plan,
  isLoading,
  onClose,
  onSave,
}) => {
  const { t } = useI18n();

  const [name, setName] = useState(plan.name);
  const [planType, setPlanType] = useState(plan.planType);
  const [price, setPrice] = useState(plan.price);
  const [billingCycle, setBillingCycle] = useState(plan.billingCycle);
  const [creditsIncluded, setCreditsIncluded] = useState(plan.creditsIncluded);
  const [featuresText, setFeaturesText] = useState((plan.features || []).join('\n'));
  const [stripePriceId, setStripePriceId] = useState(plan.stripePriceId || '');
  const [isActive, setIsActive] = useState(plan.isActive);
  const [isFeatured, setIsFeatured] = useState(plan.isFeatured);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const featuresArray = featuresText
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    onSave({
      name: name.trim(),
      planType,
      price: Number(price),
      billingCycle,
      creditsIncluded: Number(creditsIncluded),
      features: featuresArray,
      stripePriceId: stripePriceId.trim() || null,
      isActive,
      isFeatured,
    });
  };

  const equivalentVnd = Math.round(price * USD_TO_VND_RATE);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in">
      <div className="w-full max-w-xl bg-surface border border-surface-border rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-border bg-surface-elevated">
          <div className="flex items-center gap-2">
            <Sliders className="w-5 h-5 text-primary" />
            <h3 className="text-sm font-heading font-bold text-foreground">
              {t('admin.plans.modalEditTitle')}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted-fg hover:text-foreground hover:bg-surface transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                {t('admin.plans.fieldName')}
              </label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                {t('admin.plans.fieldType')}
              </label>
              <select
                value={planType}
                onChange={(e) => setPlanType(e.target.value as any)}
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              >
                <option value="subscription">{t('admin.plans.filterSubscription')}</option>
                <option value="token_pack">{t('admin.plans.filterTokenPack')}</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                {t('admin.plans.fieldPrice')}
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                required
                value={price}
                onChange={(e) => setPrice(Number(e.target.value))}
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              />
              <span className="text-[10px] text-muted-fg mt-0.5 block">
                ≈ {equivalentVnd.toLocaleString()} ₫
              </span>
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                {t('admin.plans.fieldCycle')}
              </label>
              <select
                value={billingCycle}
                onChange={(e) => setBillingCycle(e.target.value as any)}
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              >
                <option value="monthly">{t('admin.plans.cycleMonthly')}</option>
                <option value="yearly">{t('admin.plans.cycleYearly')}</option>
                <option value="one_time">{t('admin.plans.cycleOneTime')}</option>
              </select>
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                {t('admin.plans.fieldCredits')}
              </label>
              <input
                type="number"
                min="0"
                required
                value={creditsIncluded}
                onChange={(e) => setCreditsIncluded(Number(e.target.value))}
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-muted-fg mb-1">
              {t('admin.plans.fieldStripeId')}
            </label>
            <input
              type="text"
              placeholder="e.g. price_1Q5xyz..."
              value={stripePriceId}
              onChange={(e) => setStripePriceId(e.target.value)}
              className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground font-mono focus:outline-none focus:border-primary"
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-muted-fg mb-1">
              {t('admin.plans.fieldFeatures')}
            </label>
            <textarea
              rows={4}
              value={featuresText}
              onChange={(e) => setFeaturesText(e.target.value)}
              placeholder={t('admin.plans.fieldFeaturesPlaceholder')}
              className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary font-mono"
            />
          </div>

          {/* Toggles */}
          <div className="pt-2 border-t border-surface-border space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="rounded border-surface-border text-primary focus:ring-primary"
              />
              <span className="text-xs text-foreground font-medium">
                {t('admin.plans.fieldActive')}
              </span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isFeatured}
                onChange={(e) => setIsFeatured(e.target.checked)}
                className="rounded border-surface-border text-primary focus:ring-primary"
              />
              <span className="text-xs text-foreground font-medium">
                {t('admin.plans.fieldFeatured')}
              </span>
            </label>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 pt-4 border-t border-surface-border">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-xs font-medium rounded-xl border border-surface-border text-muted-fg hover:text-foreground hover:bg-surface-elevated transition-colors"
            >
              {t('admin.plans.cancel')}
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-5 py-2 text-xs font-semibold rounded-xl bg-primary text-primary-fg hover:bg-primary/90 shadow-glow-primary transition-colors disabled:opacity-50"
            >
              {isLoading ? t('admin.plans.saving') : t('admin.plans.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- Sub-Component: Create Plan Modal ---
interface CreatePlanModalProps {
  isOpen: boolean;
  isLoading: boolean;
  onClose: () => void;
  onCreate: (payload: AdminPlanCreateInput) => void;
}

const CreatePlanModal: React.FC<CreatePlanModalProps> = ({
  isLoading,
  onClose,
  onCreate,
}) => {
  const { t } = useI18n();

  const [name, setName] = useState('');
  const [planType, setPlanType] = useState<'subscription' | 'token_pack'>('subscription');
  const [price, setPrice] = useState(29);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly' | 'one_time'>('monthly');
  const [creditsIncluded, setCreditsIncluded] = useState(5000);
  const [featuresText, setFeaturesText] = useState(
    '5,000 Credits / month\nFull RAG Knowledge Base\nBYOK unlocked',
  );
  const [stripePriceId, setStripePriceId] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isFeatured, setIsFeatured] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const featuresArray = featuresText
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    onCreate({
      name: name.trim(),
      planType,
      price: Number(price),
      billingCycle,
      creditsIncluded: Number(creditsIncluded),
      features: featuresArray,
      stripePriceId: stripePriceId.trim() || null,
      isActive,
      isFeatured,
    });
  };

  const equivalentVnd = Math.round(price * USD_TO_VND_RATE);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in">
      <div className="w-full max-w-xl bg-surface border border-surface-border rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-border bg-surface-elevated">
          <div className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-primary" />
            <h3 className="text-sm font-heading font-bold text-foreground">
              {t('admin.plans.modalCreateTitle')}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted-fg hover:text-foreground hover:bg-surface transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                {t('admin.plans.fieldName')}
              </label>
              <input
                type="text"
                required
                placeholder="e.g. Pro Monthly, Starter Pack"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                {t('admin.plans.fieldType')}
              </label>
              <select
                value={planType}
                onChange={(e) => setPlanType(e.target.value as any)}
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              >
                <option value="subscription">{t('admin.plans.filterSubscription')}</option>
                <option value="token_pack">{t('admin.plans.filterTokenPack')}</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                {t('admin.plans.fieldPrice')}
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                required
                value={price}
                onChange={(e) => setPrice(Number(e.target.value))}
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              />
              <span className="text-[10px] text-muted-fg mt-0.5 block">
                ≈ {equivalentVnd.toLocaleString()} ₫
              </span>
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                {t('admin.plans.fieldCycle')}
              </label>
              <select
                value={billingCycle}
                onChange={(e) => setBillingCycle(e.target.value as any)}
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              >
                <option value="monthly">{t('admin.plans.cycleMonthly')}</option>
                <option value="yearly">{t('admin.plans.cycleYearly')}</option>
                <option value="one_time">{t('admin.plans.cycleOneTime')}</option>
              </select>
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                {t('admin.plans.fieldCredits')}
              </label>
              <input
                type="number"
                min="0"
                required
                value={creditsIncluded}
                onChange={(e) => setCreditsIncluded(Number(e.target.value))}
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-muted-fg mb-1">
              {t('admin.plans.fieldStripeId')}
            </label>
            <input
              type="text"
              placeholder="e.g. price_1Q5xyz... (optional for bank transfer only)"
              value={stripePriceId}
              onChange={(e) => setStripePriceId(e.target.value)}
              className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground font-mono focus:outline-none focus:border-primary"
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-muted-fg mb-1">
              {t('admin.plans.fieldFeatures')}
            </label>
            <textarea
              rows={4}
              value={featuresText}
              onChange={(e) => setFeaturesText(e.target.value)}
              placeholder={t('admin.plans.fieldFeaturesPlaceholder')}
              className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary font-mono"
            />
          </div>

          {/* Toggles */}
          <div className="pt-2 border-t border-surface-border space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="rounded border-surface-border text-primary focus:ring-primary"
              />
              <span className="text-xs text-foreground font-medium">
                {t('admin.plans.fieldActive')}
              </span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isFeatured}
                onChange={(e) => setIsFeatured(e.target.checked)}
                className="rounded border-surface-border text-primary focus:ring-primary"
              />
              <span className="text-xs text-foreground font-medium">
                {t('admin.plans.fieldFeatured')}
              </span>
            </label>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 pt-4 border-t border-surface-border">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-xs font-medium rounded-xl border border-surface-border text-muted-fg hover:text-foreground hover:bg-surface-elevated transition-colors"
            >
              {t('admin.plans.cancel')}
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-5 py-2 text-xs font-semibold rounded-xl bg-primary text-primary-fg hover:bg-primary/90 shadow-glow-primary transition-colors disabled:opacity-50"
            >
              {isLoading ? t('admin.plans.saving') : t('admin.plans.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- Sub-Component: Delete Plan Modal ---
interface DeletePlanModalProps {
  plan: AdminPlanItem;
  isLoading: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const DeletePlanModal: React.FC<DeletePlanModalProps> = ({
  plan,
  isLoading,
  onClose,
  onConfirm,
}) => {
  const { t } = useI18n();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in">
      <div className="w-full max-w-md bg-surface border border-surface-border rounded-2xl shadow-2xl overflow-hidden p-6 space-y-4">
        <div className="w-12 h-12 rounded-2xl bg-destructive/10 border border-destructive/20 flex items-center justify-center text-destructive mx-auto">
          <AlertTriangle className="w-6 h-6" />
        </div>

        <div className="text-center space-y-1">
          <h3 className="text-base font-heading font-bold text-foreground">
            {t('admin.plans.confirmDeleteTitle')}
          </h3>
          <p className="text-xs text-muted-fg">
            {t('admin.plans.confirmDeleteDesc', { name: plan.name })}
          </p>
        </div>

        <div className="p-3 rounded-xl bg-surface-elevated border border-surface-border text-xs space-y-1">
          <div className="flex justify-between text-muted-fg">
            <span>Plan Name:</span>
            <span className="font-semibold text-foreground">{plan.name}</span>
          </div>
          <div className="flex justify-between text-muted-fg">
            <span>Price:</span>
            <span className="font-mono text-foreground">${plan.price} / {plan.billingCycle}</span>
          </div>
          <div className="flex justify-between text-muted-fg">
            <span>Included Credits:</span>
            <span className="font-mono text-foreground">{plan.creditsIncluded.toLocaleString()}</span>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-xs font-medium rounded-xl border border-surface-border text-muted-fg hover:text-foreground hover:bg-surface-elevated transition-colors"
          >
            {t('admin.plans.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className="px-5 py-2 text-xs font-semibold rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors disabled:opacity-50"
          >
            {isLoading ? t('admin.plans.deleting') : t('admin.plans.delete')}
          </button>
        </div>
      </div>
    </div>
  );
};
